// frontend/src/modules/expenses/pages/ApprovalChainsPage.tsx
import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Plus, Trash2, X, Check, GitBranch, ArrowDown, ArchiveRestore } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { VISUAL } from '../../../design-system/visual';
import { Eyebrow, GlassCard } from '../../../design-system/pds';
import { approvalChainsApi } from '../api';
import type { ApprovalChain, ChainLevel } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

export function ApprovalChainsPage() {
  const [showInactive, setShowInactive] = React.useState(false);
  const { data, loading, reload } = useApi<{ rows: ApprovalChain[] }>(
    `/api/expenses/approval-chains${showInactive ? '?include_inactive=true' : ''}`,
  );
  const chains = data?.rows ?? [];
  const employeesQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const approvers = (employeesQ.data?.employees ?? []).filter((e) => e.user_id);

  const [editing, setEditing] = React.useState<ApprovalChain | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [restoringId, setRestoringId] = React.useState<string | null>(null);

  async function reactivate(ch: ApprovalChain) {
    setRestoringId(ch.id);
    try {
      await approvalChainsApi.update(ch.id, { is_active: true });
      toast.success(`"${ch.name}" reactivated`);
      reload();
    } catch (err: any) {
      toast.error('Reactivate failed', { description: err.message });
    } finally {
      setRestoringId(null);
    }
  }

  const nameByUser = React.useMemo(() => {
    const m = new Map<string, string>();
    approvers.forEach((a) => m.set(a.user_id, a.name));
    return m;
  }, [approvers]);

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow={
          <>
            <Link to="/dashboard/expenses" className="hover:text-foreground transition-colors">Expenses</Link>
            {' / Approval chains'}
          </>
        }
        title="Approval chains"
        description="Define multi-level approval routes. Each level's approver must sign off in order before an expense is fully approved."
        actions={
          <Button size="default" className="gap-1.5" onClick={() => { setAdding(true); setEditing(null); }} disabled={adding}>
            <Plus className="h-3.5 w-3.5" /> New chain
          </Button>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl space-y-5">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-saffron"
          />
          <span className="text-[12.5px] font-semibold text-ink/65 dark:text-white/65">Show deleted chains</span>
        </label>

        {adding && (
          <ChainEditor
            approvers={approvers}
            onCancel={() => setAdding(false)}
            onSaved={() => { setAdding(false); reload(); }}
          />
        )}

        <GlassCard padding="p-0" className="overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-[13px] text-ink/50 dark:text-white/50">Loading…</div>
          ) : chains.length === 0 && !adding ? (
            <EmptyState onAdd={() => setAdding(true)} />
          ) : (
            <ul className="divide-y divide-ink/6 dark:divide-white/8">
              {chains.map((ch) =>
                editing?.id === ch.id ? (
                  <li key={ch.id} className="px-5 py-4">
                    <ChainEditor
                      chain={ch}
                      approvers={approvers}
                      onCancel={() => setEditing(null)}
                      onSaved={() => { setEditing(null); reload(); }}
                    />
                  </li>
                ) : (
                  <motion.li
                    key={ch.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={['flex items-start gap-4 px-5 py-4', ch.is_active === false ? 'opacity-70' : ''].join(' ')}
                  >
                    <div className="h-10 w-10 rounded-2xl bg-coral/12 flex items-center justify-center shrink-0">
                      <GitBranch className="h-4 w-4 text-coral-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-semibold text-ink dark:text-white">{ch.name}</p>
                      <ol className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {ch.levels.map((l, idx) => (
                          <React.Fragment key={l.level}>
                            <span className="inline-flex items-center gap-1 rounded-full bg-white/60 dark:bg-white/[0.06] px-2.5 py-1 text-[12px] text-ink/75 dark:text-white/75">
                              <span className="text-[10px] font-bold text-coral-600">{idx + 1}</span>
                              {nameByUser.get(l.approver_user_id) ?? l.approver_user_id.slice(0, 8)}
                            </span>
                            {idx < ch.levels.length - 1 && <span className="text-ink/30 dark:text-white/30">→</span>}
                          </React.Fragment>
                        ))}
                      </ol>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {ch.is_active === false ? (
                        <>
                          <span className="inline-flex items-center rounded-full bg-ink/8 dark:bg-white/10 text-ink/55 dark:text-white/55 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide">
                            Deleted
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={restoringId === ch.id}
                            onClick={() => reactivate(ch)}
                            className="rounded-full gap-1.5 text-[12px] font-semibold"
                          >
                            <ArchiveRestore className="h-3.5 w-3.5" />
                            {restoringId === ch.id ? 'Restoring…' : 'Reactivate'}
                          </Button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => { setEditing(ch); setAdding(false); }}
                            className="text-[12px] font-semibold text-ink/65 dark:text-white/65 hover:text-coral-600 px-2 py-1"
                          >
                            Edit
                          </button>
                          <DeleteChainButton chain={ch} onDeleted={reload} />
                        </>
                      )}
                    </div>
                  </motion.li>
                ),
              )}
            </ul>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

/* ───────────────────────────── inner bits ───────────────────────────── */

function ChainEditor({
  chain,
  approvers,
  onCancel,
  onSaved,
}: {
  chain?: ApprovalChain;
  approvers: EmployeeWithUser[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = React.useState(chain?.name ?? '');
  const [levels, setLevels] = React.useState<string[]>(
    chain ? chain.levels.map((l) => l.approver_user_id) : [''],
  );
  const [busy, setBusy] = React.useState(false);

  function setLevel(i: number, userId: string) {
    setLevels((ls) => ls.map((l, idx) => (idx === i ? userId : l)));
  }
  function addLevel() { setLevels((ls) => [...ls, '']); }
  function removeLevel(i: number) { setLevels((ls) => ls.filter((_, idx) => idx !== i)); }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    const picked = levels.filter((l) => l);
    if (picked.length === 0) { toast.error('Add at least one approval level'); return; }
    const chainLevels: ChainLevel[] = picked.map((approver_user_id, level) => ({ level, approver_user_id }));
    setBusy(true);
    try {
      if (chain) {
        await approvalChainsApi.update(chain.id, { name: name.trim(), levels: chainLevels });
        toast.success('Chain updated');
      } else {
        await approvalChainsApi.create({ name: name.trim(), levels: chainLevels });
        toast.success('Chain created');
      }
      onSaved();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className={[VISUAL.bento, 'p-5 space-y-4'].join(' ')}>
      <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-[0.12em]">Chain name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Manager → Finance" autoFocus required />
      </div>

      <div className="space-y-2">
        <Label className="text-[11px] uppercase tracking-[0.12em]">Approval levels (in order)</Label>
        {levels.map((userId, i) => (
          <div key={i}>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-coral/12 text-[11px] font-bold text-coral-600 shrink-0">
                {i + 1}
              </span>
              <Select value={userId || undefined} onValueChange={(v) => setLevel(i, v)}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Pick an approver…" /></SelectTrigger>
                <SelectContent>
                  {approvers.length === 0 ? (
                    <SelectItem value="__none__" disabled>No team members found</SelectItem>
                  ) : (
                    approvers.map((a) => (
                      <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {levels.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLevel(i)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink/50 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                  aria-label={`Remove level ${i + 1}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {i < levels.length - 1 && (
              <div className="flex justify-start pl-3 my-0.5">
                <ArrowDown className="h-3 w-3 text-ink/25 dark:text-white/25" />
              </div>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addLevel}>
          <Plus className="h-3 w-3" /> Add level
        </Button>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button type="button" variant="outline" size="default" onClick={onCancel} disabled={busy} className="gap-1.5">
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
        <Button type="submit" size="default" disabled={busy} className="gap-1.5">
          <Check className="h-3.5 w-3.5" />
          {busy ? 'Saving…' : chain ? 'Save chain' : 'Create chain'}
        </Button>
      </div>
    </form>
  );
}

function DeleteChainButton({ chain, onDeleted }: { chain: ApprovalChain; onDeleted: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = React.useState(false);
  async function remove() {
    if (!(await confirm({
      title: `Delete the "${chain.name}" chain?`,
      description: 'Expenses already routed through it keep their history.',
      destructive: true,
      confirmLabel: 'Delete',
    }))) return;
    setBusy(true);
    try {
      await approvalChainsApi.remove(chain.id);
      toast.success('Chain deleted');
      onDeleted();
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={remove}
      disabled={busy}
      aria-label={`Delete ${chain.name}`}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink/55 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-50 transition-colors"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="py-16 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-coral/12 flex items-center justify-center">
        <GitBranch className="h-6 w-6 text-coral-600" strokeWidth={1.6} />
      </div>
      <Eyebrow className="mb-2">No chains yet</Eyebrow>
      <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">Route approvals through people</p>
      <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 leading-relaxed">
        Build a chain like Manager → Finance so larger expenses get the right eyes before they're approved.
      </p>
      <div className="mt-6">
        <Button onClick={onAdd} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Create a chain</Button>
      </div>
    </div>
  );
}
